/*

This component will receive a form schema and render a form
based on the schema.

The schema will be an array of objects, each object representing
a form field. It is an array on the follwing format:


  {
    name: string;
    title: string;
    description: string;
    lgWidth: number;     // from 1 to 12, default to 4
    type: "text" | "textarea" | "select" | "checkbox" | "json" | "object" | "color" | "uuid";
    default?: any;
    defaultValue?: any;
    options?: { value: string; label: string }[];
    forceGenerate?: boolean; // only for type uuid
    readOnly?: boolean; // for text, color and uuid
    required: boolean;
  }
*/

import React from 'react'
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField,
  InputAdornment,
  IconButton
} from '@material-ui/core'
import { Colorize, Autorenew, FileCopy } from '@material-ui/icons'
import JsonVisualEditor from '../JsonVisualEditor'
import ColorPicker from '../ColorPicker'
import { i18n } from '../../translate/i18n'
import { v4 as uuidv4 } from 'uuid'
import { useStyles } from './styles'
import SelectInput from '../SelectInput'
import { InputFieldText } from '../InputFieldText'
import { InputSwitch } from '../InputSwitch'

const parseJson = value => {
  if (value && typeof value === 'object') {
    return value
  }

  try {
    return JSON.parse(value)
  } catch (e) {
    return {}
  }
}

export const DynamicForm = ({
  title,
  schema,
  data,
  setData,
  onBlur,
  margin,
  variant,
  i18nBase = 'dynamicForm',
  applyDefaults = false,
  locked = false
}) => {
  const classes = useStyles()
  const [colorPickerField, setColorPickerField] = React.useState(null)

  const handleChange = event => {
    setData({
      ...data,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value
    })
  }

  const handleGenerateUuid = fieldName => {
    setData({
      ...data,
      [fieldName]: uuidv4()
    })
  }

  const handleCopyUuid = async fieldName => {
    if (!navigator?.clipboard?.writeText || !data[fieldName]) {
      return
    }

    try {
      await navigator.clipboard.writeText(data[fieldName])
    } catch (error) {
      // Clipboard access may fail because of browser permissions.
    }
  }

  React.useEffect(() => {
    if (!applyDefaults || !Array.isArray(schema) || !data) {
      return
    }

    let hasDefaultsToApply = false
    const nextData = { ...data }

    schema.forEach(field => {
      if (!field) {
        return
      }

      const defaultValue =
        field.defaultValue !== undefined ? field.defaultValue : field.default
      if (defaultValue === undefined) {
        return
      }

      const currentValue = nextData[field.name]
      const shouldSetDefault =
        currentValue === undefined ||
        currentValue === null ||
        (typeof currentValue === 'string' && currentValue.trim() === '')

      if (shouldSetDefault) {
        nextData[field.name] = defaultValue
        hasDefaultsToApply = true
      }
    })

    if (hasDefaultsToApply) {
      setData(nextData)
    }
  }, [applyDefaults, schema, data, setData])

  React.useEffect(() => {
    const hasForceGeneratedField = schema.some(
      field =>
        field?.type === 'uuid' && field.forceGenerate && !data[field.name]
    )
    if (!hasForceGeneratedField) {
      return
    }

    const nextData = { ...data }
    schema.forEach(field => {
      if (
        field?.type === 'uuid' &&
        field.forceGenerate &&
        !nextData[field.name]
      ) {
        nextData[field.name] = uuidv4()
      }
    })

    setData(nextData)
  }, [schema, data, setData])

  return (
    <Grid
      container
      spacing={1}
      style={{ display: 'flex', alignItems: 'flex-end' }}
    >
      {title && (
        <Grid item xs={12}>
          <h2>{title}</h2>
        </Grid>
      )}
      {schema.map((field, index) => {
        if (!field) {
          return <div key={index} className={classes.forceNewLine} />
        }

        return (
          <Grid
            key={field.name}
            item
            md={field.mdWidth || field.lgWidth || 4}
            sm={field.smWidth || field.mdWidth || field.lgWidth || 6}
            xs={12}
          >
            <FormControl className={classes.maxWidth}>
              {field.type === 'text' ? (
                <InputFieldText
                  name={field.name}
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  id={field.name}
                  value={data[field.name] || ''}
                  onChange={handleChange}
                  onBlur={onBlur}
                  InputProps={{
                    readOnly: !!field.readOnly
                  }}
                  className={classes.textField}
                  disabled={locked}
                />
              ) : field.type === 'textarea' ? (
                <InputFieldText
                  name={field.name}
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  id={field.name}
                  value={data[field.name] || ''}
                  onChange={handleChange}
                  onBlur={onBlur}
                  className={classes.textField}
                  multiline
                  rows={4}
                  disabled={locked}
                />
              ) : field.type === 'select' ? (
                <SelectInput
                  name={field.name}
                  labelId={`select-${field.name}`}
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  value={data[field.name] || ''}
                  onChange={handleChange}
                  onBlur={onBlur}
                  disabled={locked}
                >
                  {field.options.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectInput>
              ) : field.type === 'checkbox' ? (
                <InputSwitch
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  name={field.name}
                  id={field.name}
                  checked={!!data[field.name]}
                  onChange={e => {
                    setData({
                      ...data,
                      [field.name]: e?.target?.checked ?? !data[field.name]
                    })
                  }}
                  onBlur={onBlur}
                  disabled={locked}
                  size="small"
                />
              ) : field.type === 'json' ? (
                <JsonVisualEditor
                  title={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  value={parseJson(data[field.name]) || {}}
                  onChange={value =>
                    setData({
                      ...data,
                      [field.name]: JSON.stringify(value, null, 2)
                    })
                  }
                  disabled={locked}
                />
              ) : field.type === 'object' ? (
                <JsonVisualEditor
                  title={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  value={parseJson(data[field.name]) || {}}
                  onChange={value =>
                    setData({
                      ...data,
                      [field.name]: value
                    })
                  }
                  disabled={locked}
                />
              ) : field.type === 'color' ? (
                <InputFieldText
                  name={field.name}
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  id={field.name}
                  value={data[field.name] || '#808080'}
                  onChange={handleChange}
                  onBlur={onBlur}
                  onFocus={() => {
                    setColorPickerField(field.name)
                    document.activeElement.blur()
                  }}
                  className={classes.textField}
                  InputProps={{
                    readOnly: !!field.readOnly,
                    startAdornment: (
                      <InputAdornment position="start">
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            backgroundColor: data[field.name] || '#808080'
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setColorPickerField(field.name)}
                        >
                          <Colorize fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  disabled={locked}
                />
              ) : field.type === 'uuid' ? (
                <InputFieldText
                  name={field.name}
                  label={i18n.t(`${i18nBase}.${field.name}`, field.title)}
                  id={field.name}
                  variant={variant || 'standard'}
                  margin={margin || 'dense'}
                  value={data[field.name] || ''}
                  onChange={handleChange}
                  onBlur={onBlur}
                  className={classes.textField}
                  InputProps={{
                    readOnly: !!field.readOnly,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          title="Generate UUID"
                          aria-label="Generate UUID"
                          onClick={() => handleGenerateUuid(field.name)}
                        >
                          <Autorenew fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title={i18n.t('common.copy')}
                          aria-label={i18n.t('common.copy')}
                          onClick={() => handleCopyUuid(field.name)}
                          disabled={!data[field.name]}
                        >
                          <FileCopy fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  locked={locked}
                />
              ) : null}
            </FormControl>
            {field.description ? (
              <div className={classes.fieldDescription}>
                {field.description}
              </div>
            ) : null}
          </Grid>
        )
      })}
      <ColorPicker
        open={!!colorPickerField}
        currentColor={data[colorPickerField] || '#808080'}
        handleClose={() => setColorPickerField(null)}
        onChange={color => {
          if (!colorPickerField) {
            return
          }

          setData({
            ...data,
            [colorPickerField]: color
          })
        }}
        disabled={locked}
      />
    </Grid>
  )
}
